import { CircularProgress } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import RoundedButton from '@components/RoundedButton';

import { PdfPageWrapper, PdfPrintGlobalStyle } from './PdfExport.styles';
import {
  PdfExportErrorState,
  PdfExportLoadingState,
} from './PdfExportPage.style';
import PdfPrintControls from './PdfPrintControls';
import { useAutoPrint } from './useAutoPrint';
import { usePdfDocumentTitle } from './usePdfDocumentTitle';

const messages = defineMessages({
  loading: {
    id: 'pdfExport.loading',
    defaultMessage: 'Loading…',
  },
  error: {
    id: 'pdfExport.error',
    defaultMessage: 'Unable to load data. Please go back and try again.',
  },
});

interface PdfExportPageProps {
  /**
   * Document title applied while this page is mounted.
   * Also becomes the browser's default "Save as" filename.
   * Pass `null` (or omit) to leave the current title unchanged.
   */
  documentTitle?: string | null;

  /** Whether the underlying data fetch is still in-flight. */
  isLoading: boolean;

  /**
   * Whether data has finished loading and is valid.
   * When this first becomes `true`, auto-print fires (once).
   */
  isReady: boolean;

  /** PDF template content — rendered inside the A4 wrapper when `isReady`. */
  children: React.ReactNode;

  /** Called when the Back button is pressed (and in the default error state). */
  onBack: () => void;

  /** Label for the Back / cancel button (must be translated by the caller). */
  backLabel: string;

  /** Label for the Print / Save PDF button (must be translated by the caller). */
  printLabel: string;

  /**
   * Loading message shown in the default loading shell.
   * Omit to use the shared `pdfExport.loading` string.
   */
  loadingText?: string;

  /**
   * Error message shown in the default error shell.
   * Omit to use the shared `pdfExport.error` string.
   */
  errorText?: string;

  /**
   * Fully custom loading UI (replaces the default shell entirely).
   */
  loadingContent?: React.ReactNode;

  /**
   * Fully custom error / empty-state UI (replaces the default shell entirely).
   */
  errorContent?: React.ReactNode;
}

/**
 * Generic PDF export page shell.
 *
 * Responsibilities:
 * - Renders the sticky print-controls bar (hidden in `@media print`)
 * - Shows a loading state while data is in-flight
 * - Shows an error/empty state when data is unavailable
 * - Sets `document.title` (PDF filename) once data is ready; restores on unmount
 * - Triggers `window.print()` once after fonts are ready and layout has painted
 * - Wraps the template content in the A4 `PdfPageWrapper` + global print CSS
 */
export default function PdfExportPage({
  documentTitle = null,
  isLoading,
  isReady,
  children,
  onBack,
  backLabel,
  printLabel,
  loadingText,
  errorText,
  loadingContent,
  errorContent,
}: PdfExportPageProps) {
  const { formatMessage } = useIntl();

  usePdfDocumentTitle(isReady ? (documentTitle ?? null) : null);
  useAutoPrint({ isReady });

  const resolvedLoadingText = loadingText ?? formatMessage(messages.loading);
  const resolvedErrorText = errorText ?? formatMessage(messages.error);

  const defaultLoading = (
    <PdfExportLoadingState>
      <CircularProgress size={24} />
      <span>{resolvedLoadingText}</span>
    </PdfExportLoadingState>
  );

  const defaultError = (
    <PdfExportErrorState>
      <span>{resolvedErrorText}</span>
      <RoundedButton autoSize onClick={onBack} variant="grey">
        {backLabel}
      </RoundedButton>
    </PdfExportErrorState>
  );

  return (
    <div>
      <PdfPrintControls
        backLabel={backLabel}
        onBack={onBack}
        printLabel={printLabel}
      />

      {isLoading ? (
        (loadingContent ?? defaultLoading)
      ) : !isReady ? (
        (errorContent ?? defaultError)
      ) : (
        <>
          <PdfPrintGlobalStyle />
          <PdfPageWrapper>{children}</PdfPageWrapper>
        </>
      )}
    </div>
  );
}
